import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TopNavigation from '../../TopNavigation';

// Generate magazine covers data from Volume 1 Issue 1 to Volume 3 Issue 4
const generateMagazineCovers = () => {
  const covers = [];
  let id = 1;
  
  // Volume 1: Issues 1-12
  for (let issue = 1; issue <= 12; issue++) {
    const volume = 1;
    const issueStr = issue.toString().padStart(2, '0');
    covers.push({
      id: id++,
      volume,
      issue,
      title: `Science Fiction Monthly - Volume ${volume}, Issue ${issue}`,
      cover: `/blog_images/${volume.toString().padStart(2, '0')}_${issueStr}.png`,
      description: `Volume ${volume}, Issue ${issue}`
    });
  }
  
  // Volume 2: Issues 1-12
  for (let issue = 1; issue <= 12; issue++) {
    const volume = 2;
    const issueStr = issue.toString().padStart(2, '0');
    covers.push({
      id: id++,
      volume,
      issue,
      title: `Science Fiction Monthly - Volume ${volume}, Issue ${issue}`,
      cover: `/blog_images/${volume.toString().padStart(2, '0')}_${issueStr}.png`,
      description: `Volume ${volume}, Issue ${issue}`
    });
  }
  
  // Volume 3: Issues 1-4
  for (let issue = 1; issue <= 4; issue++) {
    const volume = 3;
    const issueStr = issue.toString().padStart(2, '0');
    covers.push({
      id: id++,
      volume,
      issue,
      title: `Science Fiction Monthly - Volume ${volume}, Issue ${issue}`,
      cover: `/blog_images/${volume.toString().padStart(2, '0')}_${issueStr}.png`,
      description: `Volume ${volume}, Issue ${issue}`
    });
  }
  
  return covers;
};

const magazineCovers = generateMagazineCovers();

const MagazineBrowser: React.FC = () => {
  const [selectedCover, setSelectedCover] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Update page title and meta tags for SEO
  useEffect(() => {
    const title = "Science Fiction Monthly - Complete Cover Gallery - Dimension Flux";
    const description = "Browse the complete collection of Science Fiction Monthly magazine covers in an interactive gallery.";
    const url = "https://dimensionflux.com/blog/science-fiction-monthly-gallery";
    const imageUrl = "https://dimensionflux.com/blog_images/01_01.png";
    
    document.title = title;
    
    // Update or create meta tags
    const updateMetaTag = (property: string, content: string, isProperty = true) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Open Graph tags
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:image', imageUrl);
    updateMetaTag('og:url', url);
    updateMetaTag('og:type', 'article');
    updateMetaTag('og:site_name', 'Dimension Flux');
    updateMetaTag('og:updated_time', new Date('2025-10-23').toISOString());

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', false);
    updateMetaTag('twitter:title', title, false);
    updateMetaTag('twitter:description', description, false);
    updateMetaTag('twitter:image', imageUrl, false);
    updateMetaTag('twitter:site', '@dimensionflux', false);
    updateMetaTag('twitter:creator', '@dimensionflux', false);

    // Standard meta description
    updateMetaTag('description', description, false);

    // Article structured data
    const existingScript = document.getElementById('article-structured-data');
    if (existingScript) {
      existingScript.remove();
    }
    const script = document.createElement('script');
    script.id = 'article-structured-data';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: title,
      description: description,
      image: imageUrl,
      datePublished: '2025-10-23',
      dateModified: '2025-10-23',
      author: {
        '@type': 'Person',
        name: 'N.M.A. Pitman'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Dimension Flux',
        logo: {
          '@type': 'ImageObject',
          url: 'https://dimensionflux.com/res.png?v=4'
        }
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url
      }
    });
    document.head.appendChild(script);
  }, []);

  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;

  // Handle touch events for swiping
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (selectedCover) {
      const currentIndex = magazineCovers.findIndex((c) => c.id === selectedCover);
      const hasPrevious = currentIndex > 0;
      const hasNext = currentIndex < magazineCovers.length - 1;

      if (isLeftSwipe && hasNext) {
        setSelectedCover(magazineCovers[currentIndex + 1].id);
      } else if (isRightSwipe && hasPrevious) {
        setSelectedCover(magazineCovers[currentIndex - 1].id);
      }
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Handle keyboard navigation when modal is open
  useEffect(() => {
    if (!selectedCover) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const currentIndex = magazineCovers.findIndex((c) => c.id === selectedCover);
      const hasPrevious = currentIndex > 0;
      const hasNext = currentIndex < magazineCovers.length - 1;

      if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        setSelectedCover(magazineCovers[currentIndex - 1].id);
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        setSelectedCover(magazineCovers[currentIndex + 1].id);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedCover(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCover]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <TopNavigation />
      
      <div className="pt-0 pb-2 px-2 sm:pt-0 sm:pb-4 sm:px-4">
        <div className="max-w-xl mx-auto w-full">
          <div className="bg-white bg-opacity-95 rounded-lg p-3 sm:p-6 border-2 border-gray-300 shadow-lg mb-4 sm:mb-6 w-full">
            <Link 
              to="/blog" 
              className="inline-block mb-4 text-gray-700 hover:text-gray-900 underline text-sm sm:text-base"
            >
              ← Back to Blog
            </Link>

            <div>
              <div className="flex gap-4 mb-4 items-start">
                <div className="flex-shrink-0">
                  <img 
                    src="/blog_images/01_01.png" 
                    alt="Science Fiction Monthly - Complete Cover Gallery"
                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-gray-200"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-oswald font-bold mb-2 text-gray-900">
                    Science Fiction Monthly - Complete Cover Gallery
                  </h1>
                </div>
              </div>
              
              <div className="text-sm text-gray-600 mb-6 pb-4 border-b border-gray-300">
                {new Date('2025-10-23').toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} • N.M.A. Pitman
              </div>

              {/* Introduction */}
              <div className="mb-6 text-gray-700 font-lora text-sm sm:text-base leading-relaxed space-y-4">
                <p>
                  In the early 1970s, London publisher New English Library (NEL) ran a successful line of science fiction paperbacks featuring authors like Frank Herbert, Robert A. Heinlein, and A.E. Van Vogt. The covers by artists including Bruce Pennington, Chris Foss, Jim Burns, and Roger Dean were very popular. NEL kept getting requests for copies of the art, so they launched "Science Fiction Monthly" in 1974 to make the artwork available as posters while also publishing fiction.
                </p>
                
                <div className="my-6 flex gap-4 justify-center">
                  <img 
                    src="/blog_images/book1.jpg" 
                    alt="Sample case spread showing headline and incident report" 
                    className="w-1/2 max-w-xs rounded-lg shadow-md"
                  />
                  <img 
                    src="/blog_images/book2.jpg" 
                    alt="Interior page showing clues and suspects tables" 
                    className="w-1/2 max-w-xs rounded-lg shadow-md"
                  />
                </div>
                
                <p>
                  Interest in science fiction art was growing when the magazine launched, and sales started strong—circulation hit 150,000 by the third issue. But the UK's economic troubles in the 1970s, combined with rising paper costs, hit hard. The price doubled within two years, and by then circulation had dropped to under 20,000.
                </p>
                <p>
                  The publication was cancelled in early 1976, but it's fondly remembered. Some of the imagery and fiction helped inspire Dimension Flux. I've collected the full set of cover images here, drawing from my own collection and the Internet Archive.
                </p>
              </div>

              <div className="flex justify-center gap-2 mb-6">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-4 py-2 rounded ${
                    viewMode === 'grid'
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors text-sm font-semibold`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 rounded ${
                    viewMode === 'list'
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors text-sm font-semibold`}
                >
                  List
                </button>
              </div>

          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {magazineCovers.map((cover) => (
                <div
                  key={cover.id}
                  onClick={() => setSelectedCover(cover.id)}
                  className="cursor-pointer bg-white rounded-lg overflow-hidden hover:scale-105 transition-transform duration-200 border-2 border-gray-200 hover:border-gray-400 shadow-md"
                >
                  <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center overflow-hidden p-2">
                    <img 
                      src={cover.cover} 
                      alt={cover.title}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        // Fallback if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = '<div class="text-center p-4"><div class="text-4xl mb-2">📚</div><div class="text-xs text-gray-700 font-oswald uppercase">Volume ' + cover.volume + ', Issue ' + cover.issue + '</div></div>';
                                   parent.className = 'aspect-[3/4] bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center';
                        }
                      }}
                    />
                  </div>
                  <div className="p-2 text-xs text-gray-600 text-center bg-gray-50">
                    Vol. {cover.volume}, Issue {cover.issue}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {magazineCovers.map((cover) => (
                <div
                  key={cover.id}
                  onClick={() => setSelectedCover(cover.id)}
                  className="cursor-pointer bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border-2 border-gray-200 hover:border-gray-400 flex items-center gap-4 shadow-sm"
                >
                  <div className="w-32 h-40 sm:w-40 sm:h-52 bg-gray-100 rounded flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-200 p-2">
                    <img 
                      src={cover.cover} 
                      alt={cover.title}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = '<div class="text-3xl">📚</div>';
                                   parent.className = 'w-32 h-40 sm:w-40 sm:h-52 bg-gradient-to-br from-gray-200 to-gray-300 rounded flex items-center justify-center flex-shrink-0';
                        }
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-oswald font-bold text-lg mb-1 text-gray-900">{cover.title}</h3>
                    <p className="text-gray-600 text-sm">{cover.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedCover && (() => {
            const currentIndex = magazineCovers.findIndex((c) => c.id === selectedCover);
            const currentCover = magazineCovers[currentIndex];
            const hasPrevious = currentIndex > 0;
            const hasNext = currentIndex < magazineCovers.length - 1;
            
            const goToPrevious = () => {
              if (hasPrevious) {
                setSelectedCover(magazineCovers[currentIndex - 1].id);
              }
            };
            
            const goToNext = () => {
              if (hasNext) {
                setSelectedCover(magazineCovers[currentIndex + 1].id);
              }
            };
            
            return (
              <div
                className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-1 sm:p-4"
                onClick={() => setSelectedCover(null)}
              >
                <div
                  className="bg-white rounded-lg p-1 sm:p-6 max-w-7xl w-full sm:w-[90vw] border-2 border-gray-300 shadow-xl h-[98vh] sm:h-[90vh] flex flex-col relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header - fixed at top */}
                  <div className="flex justify-between items-start mb-0.5 sm:mb-3 flex-shrink-0 px-1 sm:px-0">
                    <div className="flex-1 pr-2 min-w-0">
                      <h2 className="text-base sm:text-xl font-oswald font-bold text-gray-900 mb-0 sm:mb-1">
                        Science Fiction Monthly
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-600 font-oswald">
                        Volume {currentCover.volume}, Issue {currentCover.issue}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedCover(null)}
                      className="text-gray-600 hover:text-gray-900 text-2xl flex-shrink-0 ml-2"
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  
                  {/* Image container - flexible, minimal padding on mobile */}
                  <div 
                    className="relative flex-1 min-h-0 flex items-center justify-center mb-0.5 sm:mb-3 overflow-hidden"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                  >
                    {/* Desktop navigation buttons - on sides */}
                    {hasPrevious && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          goToPrevious();
                        }}
                        className="hidden sm:block absolute left-0 -left-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 border-2 border-gray-300 z-10 flex-shrink-0"
                        aria-label="Previous cover"
                      >
                        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    )}
                    
                    <div className="w-full h-full max-h-full flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden border border-gray-200 p-0.5 sm:p-2">
                      <img 
                        src={currentCover.cover} 
                        alt={currentCover.title}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div class="text-center"><div class="text-6xl mb-4">📚</div><div class="text-gray-700 font-oswald uppercase text-lg">Volume ' + currentCover.volume + ', Issue ' + currentCover.issue + '</div></div>';
                                   parent.className = 'w-full h-full max-h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg';
                          }
                        }}
                      />
                    </div>
                    
                    {hasNext && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          goToNext();
                        }}
                        className="hidden sm:block absolute right-0 -right-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 border-2 border-gray-300 z-10 flex-shrink-0"
                        aria-label="Next cover"
                      >
                        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  {/* Mobile navigation buttons - below image */}
                  <div className="flex sm:hidden justify-center items-center gap-4 mb-0.5 flex-shrink-0">
                    {hasPrevious && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          goToPrevious();
                        }}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full p-3 shadow-md border border-gray-300"
                        aria-label="Previous cover"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    )}
                    {hasNext && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          goToNext();
                        }}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full p-3 shadow-md border border-gray-300"
                        aria-label="Next cover"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  {/* Footer - fixed at bottom */}
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-0.5 sm:gap-0 flex-shrink-0 pt-0.5 sm:pt-2 border-t border-gray-200 px-1 sm:px-0">
                    <p className="text-gray-600 text-center text-xs sm:text-base">
                      {currentCover.description}
                    </p>
                    <span className="text-gray-400 text-xs sm:text-sm">
                      {currentIndex + 1} of {magazineCovers.length}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MagazineBrowser;
